import { memo } from "react";
import { styled } from "styled-components";

import { ImageBackground, PlayIconCircled, SourceLabel } from "common/components";
import { lineTruncationStyle } from "common/styles";
import { useDispatch } from "common/utilities/react-redux";
import { useGo } from "common/utilities/useGo";
import type { CustomKnowledge } from "domains/customContentAndSources/types";
import { StyledContent, StyledTitle } from "domains/knowledgeFeed/components/shared";
import { KnowledgeDetailButton } from "domains/publication/components/KnowledgeDetailButton";
import { useMedia } from "domains/viewport/useMedia";
import { setVisualMediaPlayer } from "domains/visualMediaPlayer/actions";
import { MediaType } from "domains/visualMediaPlayer/types";
import type { Knowledge } from "entities/knowledge/types";

const StyledSmallPlayIcon = styled(PlayIconCircled)`
  transition: transform 0.3s;
  width: 44px;
  height: 44px;
  position: absolute;
  top: 23px; // 90 - 44 = 46 / 2 = 23
  left: 38px; // 120 - 44 = 76 / 2 = 38
  margin: auto;
`;

const StyledListLink = styled.div`
  display: flex;
  flex-direction: column;

  &:hover ${StyledSmallPlayIcon} {
    transform: scale(1.2);
  }
`;

const StyledSummary = styled.div`
  font-weight: normal;
  font-size: 0.8rem;
  line-height: 1.1rem;
  color: ${({ theme }) => theme.colors.textFade};
  margin-bottom: 5px;
  ${lineTruncationStyle(3)}
`;

const StyledRow = styled.div`
  display: flex;
`;

const StyledOuterRow = styled(StyledRow)`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const StyledImageContainer = styled.div`
  position: relative;
  width: 120px;
  min-width: 120px;
  height: 90px;
  border-radius: 5px;
  margin-right: 1rem;
`;

const StyledImage = styled(ImageBackground)`
  //width: 120px;
  //min-width: 120px;
  //height: 90px;
  //display: flex;
  //align-items: center;
  //justify-content: center;
`;

type SmallVideoItemProps = {
  isSelected?: boolean;
  knowledge: Knowledge | CustomKnowledge;
  link: string;
  onClick?: () => void;
};

export const SmallVideoItem = memo(({ knowledge, link }: SmallVideoItemProps) => {
  const dispatch = useDispatch();
  const go = useGo();
  const isSmallScreen = useMedia({ small: true, medium: true }, false);

  const { images, title, source, time } = knowledge;

  const goToVideoPageOrOpenVideoPlayer = () => {
    if (isSmallScreen) {
      go(link);
      return;
    }

    const getMediaType = () => {
      if (knowledge.embedded_media !== undefined) return MediaType.videoViaEmbeddedHtmlPlayer;
      return MediaType.videoViaDirectFileLink;
    };

    const getUrl = () => {
      if (knowledge.embedded_media?.url) return knowledge.embedded_media.url;
      if ("attachment" in knowledge && !!knowledge.attachment) return knowledge.attachment.url;
      if ("media" in knowledge && !!knowledge.media) return knowledge.media.url;
      return undefined;
    };

    dispatch(
      setVisualMediaPlayer({ id: knowledge.id, mediaType: getMediaType(), name: knowledge.title, url: getUrl() }),
    );
  };

  return (
    <StyledListLink onClick={goToVideoPageOrOpenVideoPlayer}>
      <StyledOuterRow>
        <StyledRow>
          <StyledImageContainer>
            <StyledImage src={images?.thumbnail} />
            <StyledSmallPlayIcon />
          </StyledImageContainer>
          <StyledContent>
            <StyledTitle>{title}</StyledTitle>
            {knowledge.summary && <StyledSummary>{knowledge.summary}</StyledSummary>}
            <SourceLabel date={time} hideImage source={source} type="small" />
          </StyledContent>
        </StyledRow>
        <KnowledgeDetailButton link={link} onClick={(event) => event.stopPropagation()} />
      </StyledOuterRow>
    </StyledListLink>
  );
});

SmallVideoItem.displayName = "SmallVideoItem";
